import type { IErrorApiResponse, ISuccessApiResponse } from '@wyz/types';
import {
  OverridableQuery,
  UseQueryOptions,
  restrictApi,
  serialize,
  useQuery,
} from '@wyz/utils';
import { isNil, omitBy } from 'lodash-es';
import { useApi, useConfig } from '~/common/context';
import { IWcwSearchFormSchema } from '~/purchase/schemas';
import type { IWcwOffer } from '~/purchase/types';
import { PurchaseQueryKeys } from '~/purchase/api';

type Options = {
  list?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<Array<IWcwOffer>>, IErrorApiResponse>
  > & { args: { customerId: string; query: IWcwSearchFormSchema } };
};

export const useWcwSearch = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  const listQuery = useQuery({
    queryKey: PurchaseQueryKeys.wcw.search(
      options.list?.args.customerId ?? '',
      options.list?.args.query,
    ),
    queryFn: async (): Promise<ISuccessApiResponse<Array<IWcwOffer>>> => {
      const { customerId, query } = options.list!.args;
      const params = serialize(
        omitBy(
          {
            reference: query.identifier?.value ?? query.rawIdentifier ?? null,
            compatibilityBrand: query.brand ?? null,
            compatibilityModel: query.model ?? null,
            compatibilityVersions: query.versions?.length
              ? query.versions
              : null,
            seasonPublicKeys: query.seasons,
            quantity: query.set ?? null,
          },
          isNil,
        ),
      );
      return callCoreApi<ISuccessApiResponse<Array<IWcwOffer>>>(
        `/offers/api/wtb/tenants/${tenantCode}/customers/${customerId}/wheel-offers/search?${params}`,
      );
    },
    refetchOnWindowFocus: false,
    enabled: !!options.list?.args.query,
    ...options.list,
  });

  return restrictApi(options, { list: listQuery });
};
